/**
 * The authenticated shell.
 *
 * Landmarks are the contract: one `<header role="banner">` (the top bar), one
 * `<main id="main">`, one `<nav aria-label>` per navigation, and the skip link
 * as the FIRST focusable element on every page.
 *
 * The whole page is one flex column of viewport height at minimum and the
 * content block is the only child that grows, so a page shorter than the
 * viewport pushes its footer to the bottom edge instead of stranding it
 * mid-screen above a band of empty background. `dvh` rather than `vh`: on a
 * phone the collapsing address bar is excluded from `dvh` and included in
 * `vh`, so `100vh` leaves a strip of empty page exactly as tall as the chrome
 * that just scrolled away. And `flex-grow` on the content rather than an auto
 * margin on the footer: the auto margin would eat the footer's section-gap
 * separation on every page that actually scrolls.
 *
 * There is no side column. `nav_shape` is `navbar`, so primary navigation sits
 * horizontally in the top bar and collapses to the bottom bar below the tablet
 * tier — the base tier reserves 4.5rem for that bar and gives it back at the
 * tablet tier where the bar is gone.
 */
import { Outlet } from 'react-router-dom';

import { BottomNav, Footer, Topbar, chromeCopy, useCategories } from '@shared/chrome';

export function AppShell() {
  // The tree is the shell's read, not a page's: it hangs off the navbar on
  // every route, and a page cannot inject data into chrome it does not render.
  const { data: categories } = useCategories();

  return (
    <div className="flex min-h-dvh flex-col bg-bg pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] text-ink md:pb-0">
      <a href="#main" className="skip-link">
        {chromeCopy.skipToContent}
      </a>

      <Topbar categories={categories?.items} />

      <div className="container flex flex-[1_0_auto] flex-col">
        <main id="main" className="flex-[1_0_auto] self-stretch">
          <Outlet />
        </main>
      </div>

      <Footer />
      <BottomNav categories={categories?.items} />
    </div>
  );
}
